import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, CalendarCheck, CalendarClock, Banknote, ArrowRight } from "lucide-react";
import { api, fmtINR, apiError } from "../lib/api";

const StatCard = ({ icon: Icon, label, value, sub, tone, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className="text-left bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 w-full">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-3xl font-bold text-slate-900 mt-2 font-head tabular-nums">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-5 h-5" /></div>
    </div>
  </button>
);

export default function HRDashboard() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/hr/audit?limit=25").then((r) => setAudit(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/hr/dashboard").then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-24 text-center text-slate-400" data-testid="hr-loading">Loading HR overview...</div>;
  if (!data) return null;

  const att = data.attendance_today || {};
  const attRows = ["Present", "Absent", "Half Day", "Leave", "LWP", "Holiday"];
  const attColor = { Present: "bg-emerald-100 text-emerald-700", Absent: "bg-red-100 text-red-700", "Half Day": "bg-amber-100 text-amber-700", Leave: "bg-blue-100 text-blue-700", LWP: "bg-red-100 text-red-700", Holiday: "bg-slate-100 text-slate-600" };

  return (
    <div data-testid="hr-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">HR Dashboard</h1>
        <p className="text-sm text-slate-500 mt-0.5">Workforce overview · Payroll month {data.month}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="hr-stat-employees" icon={Users} label="Active Employees" value={data.employee_count}
          sub={`${data.total_employees} total on record`} tone="bg-indigo-50 text-indigo-600" onClick={() => navigate("/employees")} />
        <StatCard testid="hr-stat-attendance" icon={CalendarCheck} label="Marked Today" value={data.marked_today}
          sub={`${att.Present || 0} present`} tone="bg-emerald-50 text-emerald-600" onClick={() => navigate("/attendance/daily")} />
        <StatCard testid="hr-stat-leaves" icon={CalendarClock} label="Pending Leaves" value={data.pending_leaves}
          sub="awaiting approval" tone="bg-amber-50 text-amber-600" onClick={() => navigate("/leaves")} />
        <StatCard testid="hr-stat-payroll" icon={Banknote} label={`Payroll (${data.month})`} value={fmtINR(data.payroll_total)}
          sub={`${data.payroll_count} slips · ${fmtINR(data.payroll_paid)} paid`} tone="bg-blue-50 text-blue-600" onClick={() => navigate("/payroll")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-900 font-head">Today's Attendance</h2>
            <button data-testid="hr-goto-attendance" onClick={() => navigate("/attendance/daily")} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1">Mark <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            {attRows.map((k) => (
              <div key={k} className={`px-3 py-2 rounded-lg text-sm font-medium ${attColor[k]}`} data-testid={`hr-att-${k.toLowerCase().replace(" ", "-")}`}>
                <span className="tabular-nums font-bold">{att[k] || 0}</span> <span className="opacity-80">{k}</span>
              </div>
            ))}
          </div>
          {data.marked_today === 0 && <p className="text-sm text-slate-400 mt-4">No attendance marked for today yet.</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900 font-head mb-4">Headcount by Department</h2>
          <div className="space-y-2">
            {Object.entries(data.dept_counts || {}).sort((a, b) => b[1] - a[1]).map(([dept, n]) => (
              <div key={dept} className="flex items-center gap-3" data-testid={`hr-dept-${dept}`}>
                <span className="text-sm text-slate-600 w-36 truncate">{dept}</span>
                <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.max(6, (n / Math.max(1, data.employee_count)) * 100)}%` }} />
                </div>
                <span className="text-sm font-semibold text-slate-800 tabular-nums w-6 text-right">{n}</span>
              </div>
            ))}
            {!Object.keys(data.dept_counts || {}).length && <p className="text-sm text-slate-400">No employees added yet.</p>}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mt-5" data-testid="hr-audit-panel">
        <h2 className="text-base font-semibold text-slate-900 font-head mb-4">Recent HR Activity (audit trail)</h2>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {audit.length === 0 && <p className="text-sm text-slate-400">No attendance, leave or payroll changes recorded yet.</p>}
          {audit.map((a) => (
            <div key={a.id} data-testid={`hr-audit-${a.id}`} className="flex items-start gap-3 text-sm border-b border-slate-100 pb-2 last:border-0">
              <span className="text-xs font-mono text-slate-400 w-36 shrink-0">{String(a.at || "").slice(0, 16).replace("T", " ")}</span>
              <span className="font-medium text-slate-700 w-40 shrink-0 truncate">{a.user_name}</span>
              <span className="text-slate-600 flex-1">{a.detail || a.action}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
